import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import SearchableSelect from './SearchableSelect';
import RichEmailEditor from './RichEmailEditor';

import { formatDateTime } from '../utils/dateFormat';
const AUDIENCES = [
  { value: 'PARTNERS', label: 'Account Managers' },
  { value: 'ORG_ADMINS', label: 'Organization admins' },
  { value: 'ALL_USERS', label: 'All users' },
  { value: 'ORGANIZATION', label: "One organization's users" }
];
const AUDIENCE_LABEL = Object.fromEntries(AUDIENCES.map((a) => [a.value, a.label]));

const PLACEHOLDERS = [
  { token: '{{firstName}}', label: 'First name' },
  { token: '{{fullName}}', label: 'Full name' },
  { token: '{{organization}}', label: 'Organization' }
];

const STATUS_BADGE = { SENDING: 'info', COMPLETED: 'success', INTERRUPTED: 'warning', SENT: 'success', FAILED: 'danger', PENDING: 'secondary' };

const errorText = (error, fallback) => {
  const e = error.response?.data?.error;
  return typeof e === 'string' && e ? e : fallback;
};

/** System Admin broadcast email: compose with {{firstName}} personalization, pick an audience, send, review history. */
function EmailBroadcastAdmin() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('PARTNERS');
  const [organizationId, setOrganizationId] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [detail, setDetail] = useState(null);
  const bodyRef = useRef(null);

  const loadCampaigns = useCallback(async () => {
    try {
      const response = await axios.get('/api/system-admin/email-campaigns');
      setCampaigns(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // History is best-effort; the compose form still works.
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
    axios.get('/api/system-admin/organizations')
      .then((r) => setOrganizations(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOrganizations([]));
  }, [loadCampaigns]);

  // Keep history live while something is sending.
  const anySending = campaigns.some((c) => c.status === 'SENDING');
  useEffect(() => {
    if (!anySending) return undefined;
    const t = setInterval(loadCampaigns, 3000);
    return () => clearInterval(t);
  }, [anySending, loadCampaigns]);

  // Debounced preview: recipient count + the message rendered for the first recipient.
  useEffect(() => {
    if (audience === 'ORGANIZATION' && !organizationId) {
      setPreview(null);
      setPreviewError('');
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const response = await axios.post('/api/system-admin/email-campaigns/preview', {
          subject,
          body,
          bodyFormat: 'HTML',
          audience,
          organizationId: organizationId || null
        });
        setPreview(response.data);
        setPreviewError('');
      } catch (error) {
        setPreview(null);
        setPreviewError(errorText(error, 'Could not build a preview'));
      }
    }, 500);
    return () => clearTimeout(t);
  }, [subject, body, audience, organizationId]);

  const insertPlaceholder = (token) => {
    if (bodyRef.current) {
      bodyRef.current.insertText(token);
    } else {
      setBody((b) => b + token);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await axios.post('/api/system-admin/email-campaigns/test', { subject, body, bodyFormat: 'HTML' });
      toast.success(`Test email sent to ${response.data.sentTo}`);
    } catch (error) {
      toast.error(errorText(error, 'Could not send the test email'));
    } finally {
      setTesting(false);
    }
  };

  const count = preview?.recipientCount ?? 0;
  const unknown = preview?.unknownPlaceholders || [];
  const canSend =
    subject.trim() && body.trim() && preview && count > 0 && unknown.length === 0 &&
    preview.mailConfigured && !preview.tooManyRecipients && !sending;

  const handleSend = async () => {
    if (!window.confirm(`Send this email to ${count} recipient${count === 1 ? '' : 's'} (${AUDIENCE_LABEL[audience]})?`)) {
      return;
    }
    setSending(true);
    try {
      await axios.post('/api/system-admin/email-campaigns', {
        subject,
        body,
        bodyFormat: 'HTML',
        audience,
        organizationId: organizationId || null
      });
      toast.success('Sending started - progress is shown in the history below');
      setSubject('');
      setBody('');
      loadCampaigns();
    } catch (error) {
      toast.error(errorText(error, 'Could not start sending'));
    } finally {
      setSending(false);
    }
  };

  const openDetail = async (campaign) => {
    try {
      const response = await axios.get(`/api/system-admin/email-campaigns/${campaign.id}`);
      setDetail(response.data);
    } catch (error) {
      toast.error('Could not load that campaign');
    }
  };

  const orgOptions = organizations.map((o) => ({ value: o.id, label: o.name }));

  return (
    <>
      <Card className="mb-4">
        <Card.Header><h5 className="mb-0">Broadcast email</h5></Card.Header>
        <Card.Body>
          {preview && !preview.mailConfigured && (
            <Alert variant="warning">
              Email isn&apos;t configured yet. Set up SMTP under the <strong>Email</strong> tab before sending.
            </Alert>
          )}
          <Row>
            <Col lg={7}>
              <Form.Group className="mb-3" controlId="broadcast-audience">
                <Form.Label>Send to</Form.Label>
                <Form.Select value={audience} onChange={(e) => { setAudience(e.target.value); setOrganizationId(''); }}>
                  {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </Form.Select>
              </Form.Group>
              {audience === 'ORGANIZATION' && (
                <Form.Group className="mb-3">
                  <Form.Label>Organization</Form.Label>
                  <SearchableSelect
                    options={orgOptions}
                    value={organizationId}
                    onChange={setOrganizationId}
                    placeholder="Search organizations"
                  />
                </Form.Group>
              )}
              <Form.Group className="mb-3" controlId="broadcast-subject">
                <Form.Label>Subject</Form.Label>
                <Form.Control value={subject} maxLength={255} onChange={(e) => setSubject(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Message</Form.Label>
                <RichEmailEditor ref={bodyRef} value={body} onChange={setBody} />
              </Form.Group>
              <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
                <span className="small text-muted">Insert:</span>
                {PLACEHOLDERS.map((p) => (
                  <Button key={p.token} type="button" size="sm" variant="outline-secondary" onClick={() => insertPlaceholder(p.token)}>
                    {p.label}
                  </Button>
                ))}
              </div>
              <p className="small text-muted">
                <code>{'{{firstName}}'}</code> becomes each person&apos;s first name, or <em>there</em> when we don&apos;t
                have one. Add your own fallback like <code>{'{{firstName|friend}}'}</code>. Each person gets their own
                copy, so recipients never see each other&apos;s addresses.
              </p>
              <p className="small text-muted">
                Paste from a web page, an email or a document and the formatting comes along, or use the toolbar. The
                <em> HTML</em> tab shows the HTML, and you can paste HTML from an email designer there. Scripts, forms and
                similar are removed before sending, and a plain-text copy goes along for mail apps that can&apos;t show
                formatting. Check the preview: it shows the email exactly as it will arrive.
              </p>
            </Col>

            <Col lg={5}>
              <div className="border rounded p-3 mb-3">
                <div className="small text-uppercase text-muted mb-1">Recipients</div>
                {previewError && <div className="text-danger small">{previewError}</div>}
                {audience === 'ORGANIZATION' && !organizationId && <div className="text-muted">Choose an organization</div>}
                {preview && (
                  <>
                    <div className="fs-5">
                      {count} recipient{count === 1 ? '' : 's'}
                    </div>
                    {preview.tooManyRecipients && (
                      <div className="text-danger small">More than {preview.maxRecipients} - narrow the audience.</div>
                    )}
                    {count > 0 && (
                      <div className="small text-muted">
                        {preview.sample.map((s) => `${s.firstName || '—'} <${s.email}>`).join(', ')}
                        {count > preview.sample.length ? ', …' : ''}
                      </div>
                    )}
                    {count === 0 && <div className="text-muted small">No matching recipients with an email address.</div>}
                  </>
                )}
              </div>

              <div className="border rounded p-3">
                <div className="small text-uppercase text-muted mb-1">
                  Preview {preview?.renderedFor ? `for ${preview.renderedFor}` : ''}
                </div>
                {unknown.length > 0 ? (
                  <div className="text-danger small">
                    Unknown placeholder{unknown.length > 1 ? 's' : ''}: {unknown.map((u) => `{{${u}}}`).join(', ')}.
                    Supported: {'{{firstName}}, {{lastName}}, {{fullName}}, {{organization}}'}.
                  </div>
                ) : preview?.renderedBody !== undefined ? (
                  <>
                    <div className="fw-semibold mb-2">{preview.renderedSubject || <span className="text-muted">(no subject)</span>}</div>
                    {preview.renderedBody ? (
                      <iframe
                        title="Email preview"
                        sandbox=""
                        srcDoc={preview.renderedHtml}
                        className="w-100 border rounded"
                        style={{ height: 420, background: '#f3f4f6' }}
                      />
                    ) : (
                      <div className="text-muted">(empty message)</div>
                    )}
                  </>
                ) : (
                  <div className="text-muted small">Start typing to see how it will look.</div>
                )}
              </div>
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-3">
            <Button variant="outline-primary" onClick={handleTest} disabled={testing || !subject.trim() || !body.trim() || unknown.length > 0}>
              {testing ? 'Sending test…' : 'Send test to me'}
            </Button>
            <Button variant="primary" onClick={handleSend} disabled={!canSend}>
              {sending ? 'Starting…' : count ? `Send to ${count} recipient${count === 1 ? '' : 's'}` : 'Send'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><h5 className="mb-0">History</h5></Card.Header>
        <Card.Body>
          <Table responsive hover size="sm">
            <thead>
              <tr><th>Sent</th><th>Subject</th><th>Audience</th><th>Status</th><th>Delivered</th><th>Failed</th></tr>
            </thead>
            <tbody>
              {campaigns.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted">Nothing sent yet</td></tr>
              )}
              {campaigns.map((c) => (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(c)}>
                  <td className="small">{formatDateTime(c.createdAt)}</td>
                  <td>{c.subject}</td>
                  <td className="small">{AUDIENCE_LABEL[c.audience] || c.audience}</td>
                  <td><Badge bg={STATUS_BADGE[c.status] || 'secondary'}>{c.status.charAt(0) + c.status.slice(1).toLowerCase()}</Badge></td>
                  <td>{c.sentCount} / {c.totalRecipients}</td>
                  <td>{c.failedCount > 0 ? <span className="text-danger">{c.failedCount}</span> : 0}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={!!detail} onHide={() => setDetail(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{detail?.campaign?.subject}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail && (
            <>
              {detail.campaign.bodyFormat === 'HTML' ? (
                // The message as written; the server cleaned what was actually sent the same way
                // eslint-disable-next-line react/no-danger
                <div className="border rounded p-2 small mb-3" style={{ background: '#ffffff', color: '#1f2937' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(detail.campaign.body) }} />
              ) : (
                <pre className="border rounded p-2 small" style={{ whiteSpace: 'pre-wrap' }}>{detail.campaign.body}</pre>
              )}
              <Table size="sm" responsive>
                <thead><tr><th>Recipient</th><th>Status</th><th>Details</th></tr></thead>
                <tbody>
                  {detail.recipients.map((r) => (
                    <tr key={r.id}>
                      <td>{r.firstName ? `${r.firstName} ` : ''}&lt;{r.email}&gt;</td>
                      <td><Badge bg={STATUS_BADGE[r.status] || 'secondary'}>{r.status.charAt(0) + r.status.slice(1).toLowerCase()}</Badge></td>
                      <td className="small text-danger">{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default EmailBroadcastAdmin;
